import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Composable for extracting and formatting match/job data for display in quote cards
 *
 * @param {Ref<MatchData | JobData>} match - Reactive reference to match or job data object
 * @returns {Object} Computed reactive properties including dates, pricing, and type flags
 */
export function useMatchData(match) {
    const matchId = ref();
    const companyRef = ref();
    const isAnonymous = ref();
    const isLocal = ref();
    const isInterstate = ref();
    const isOrganic = ref();
    const isCustom = ref();
    const isTender = ref();
    const isLane = ref();
    const isTrip = ref();
    const isFixed = ref();
    const isVariable = ref();
    const isVisible = ref();
    const isPremium = ref();
    const reviewPricing = ref();
    const moveType = ref();
    const moveId = ref();
    const matchType = ref();
    const companyName = ref();
    const companyId = ref();
    const companyCount = ref();
    const logoUrl = ref();
    const inputPrice = ref();
    const pricePerUnit = ref();
    const pricePerHour = ref();
    const quotedSpace = ref();
    const spaceTotal = ref();
    const companyTotal = ref();
    const surcharge = ref();
    const extraSurcharge = ref();
    const bundlesCost = ref();
    const muvalFee = ref();
    const bookingFee = ref();
    const rating = ref();
    const reviewCount = ref();
    const estimatedTotal = ref();
    const minPrice = ref();
    const callOutFee = ref();
    const matchableType = ref();
    const matchableId = ref();
    const matchableFormatted = ref();
    const matchableIdFormatted = ref();
    const routeDeliveryTimeframe = ref();
    const bundles = ref();
    const minHours = ref();
    const order = ref();
    const companyNotes = ref();
    const createdAt = ref();
    const expiresAt = ref();
    const minTime = ref();

    // formatted dates
    const pickupDateFormatted = ref();
    const deliveryDateFormatted = ref();

    function convertToShortYear(dateStr) {
        if (!dateStr) return dateStr;
        // Parse DD/MM/YYYY and convert to DD/MM/YY
        const parsed = dayjs(dateStr, 'DD/MM/YYYY');
        return parsed.isValid() ? parsed.format('DD/MM/YY') : dateStr;
    }

    function execute() {
        let data = unref(match);

        companyRef.value = data?.company_ref;
        order.value = data?.order;

        console.log('useMatchData', data);

        // Boolean checks
        isAnonymous.value = data?.anonymous;
        isLocal.value = data?.move_type == 'LOCAL';
        isInterstate.value = data?.move_type == 'INTERSTATE';
        isOrganic.value = data?.match_type == 'ORGANIC';
        isCustom.value = data?.match_type == 'CUSTOM';
        isTender.value = data?.match_type === 'TENDER';
        isLane.value = data?.matchable_type == 'lane';
        isTrip.value = data?.matchable_type == 'trip';
        isFixed.value = data?.pricing_type == 'FIXED';
        isVariable.value = data?.pricing_type == 'VARIABLE';
        isVisible.value = data?.visible;
        isPremium.value = data?.premium;

        // Getters
        matchId.value = data?.id;
        moveType.value = data?.move_type;
        moveId.value = data?.move_id;
        matchType.value = data?.match_type;
        companyName.value = data?.company?.name;
        companyId.value = data?.company?.id;
        companyCount.value = data?.company_count;
        logoUrl.value = data?.company?.logo;
        pricePerUnit.value = data?.price_per_unit?.formatted;
        pricePerHour.value = data?.price_per_hour?.formatted;
        quotedSpace.value = data?.quoted_space;
        rating.value = data?.company?.rating;
        reviewCount.value = data?.company?.review_count;
        estimatedTotal.value = data?.estimated_total?.formatted;
        inputPrice.value = data?.input_price?.formatted;
        minPrice.value = data?.min_price?.formatted;
        callOutFee.value = data?.call_out_fee?.formatted;
        matchableType.value = data?.matchable_type;
        matchableId.value = data?.matchable_id;
        matchableFormatted.value = handleMatchableType(data?.matchable_type, data?.matchable);
        matchableIdFormatted.value = handleMatchableId(data?.matchable_type, data?.matchable);
        minHours.value = data?.min_hours;
        routeDeliveryTimeframe.value = data?.matchable?.route_delivery_timeframe;
        bundles.value = data?.bundles;
        spaceTotal.value = data?.space_total?.formatted;
        companyTotal.value = data?.company_total?.formatted;
        surcharge.value = data?.surcharge?.formatted;
        extraSurcharge.value = data?.extra_surcharge?.formatted;
        bundlesCost.value = data?.bundles_cost?.formatted;
        muvalFee.value = data?.muval_fee?.formatted;
        bookingFee.value = data?.booking_fee?.formatted;
        reviewPricing.value = data?.review_pricing;
        companyNotes.value = data?.company_notes;
        createdAt.value = data?.created_at;
        expiresAt.value = data?.expires_at;
        minTime.value = data?.min_time;

        // Dates
        pickupDateFormatted.value = (() => {
            // Support both match data and job data field naming
            // Priority: Use direct job dates first (they're more accurate), then fall back to match formatted dates
            // For jobs: pickup_date_from, pickup_date_by (raw ISO dates)
            // For matches: min_pickup_date_formatted, max_pickup_date_formatted
            // For jobs with match data: match_min_pickup_date_formatted, match_max_pickup_date_formatted

            // Prioritize job fields over match fields to avoid backend formatting bugs
            const minPickupRaw =
                (data?.pickup_date_from ? dayjs(data.pickup_date_from).format('DD/MM/YYYY') : null) ??
                data?.pickup_date_from_formatted ??
                data?.min_pickup_date_formatted ??
                data?.match_min_pickup_date_formatted;

            const maxPickupRaw =
                (data?.pickup_date_by ? dayjs(data.pickup_date_by).format('DD/MM/YYYY') : null) ??
                data?.pickup_date_by_formatted ??
                data?.max_pickup_date_formatted ??
                data?.match_max_pickup_date_formatted;

            const minPickup = convertToShortYear(minPickupRaw);
            const maxPickup = convertToShortYear(maxPickupRaw);

            if (isLane.value) {
                return `${minPickup}${maxPickup && maxPickup !== minPickup ? ` - ${maxPickup}` : ''}`;
            } else if (isCustom.value || isTender.value) {
                return `${minPickup}${maxPickup && maxPickup !== minPickup ? ` - ${maxPickup}` : ''}`;
            } else {
                // For all other matches (including local moves), show date range if dates differ
                return `${minPickup}${maxPickup && maxPickup !== minPickup ? ` - ${maxPickup}` : ''}`;
            }
        })();

        deliveryDateFormatted.value = (() => {
            // Support both match data and job data field naming
            // Priority: Use direct job dates first (they're more accurate), then fall back to match formatted dates
            // For jobs: delivery_date_from, delivery_date_by (raw ISO dates)
            // For matches: min_delivery_date_formatted, max_delivery_date_formatted
            // For jobs with match data: match_min_delivery_date_formatted, match_max_delivery_date_formatted

            // Prioritize job fields over match fields to avoid backend formatting bugs
            const minDeliveryRaw =
                (data?.delivery_date_from ? dayjs(data.delivery_date_from).format('DD/MM/YYYY') : null) ??
                data?.delivery_date_from_formatted ??
                data?.min_delivery_date_formatted ??
                data?.match_min_delivery_date_formatted;

            const maxDeliveryRaw =
                (data?.delivery_date_by ? dayjs(data.delivery_date_by).format('DD/MM/YYYY') : null) ??
                data?.delivery_date_by_formatted ??
                data?.max_delivery_date_formatted ??
                data?.match_max_delivery_date_formatted;

            const minDelivery = convertToShortYear(minDeliveryRaw);
            const maxDelivery = convertToShortYear(maxDeliveryRaw);
            const deliveryFlexibility =
                data?.delivery_date_flexibility_formatted ?? data?.match_delivery_date_flexibility_formatted;
            // Convert maxDeliveryString as it may contain a date
            const maxDeliveryString = convertToShortYear(data?.max_delivery_string ?? data?.match_max_delivery_string);

            if (isLane.value && !isCustom.value) {
                return maxDeliveryString;
            } else if (isCustom.value || isTender.value) {
                if (deliveryFlexibility) {
                    return deliveryFlexibility;
                }

                return `${minDelivery}${maxDelivery && maxDelivery !== minDelivery ? ` - ${maxDelivery}` : ''}`;
            } else {
                return maxDelivery ? maxDelivery : minDelivery;
            }
        })();
    }

    watch(match, execute, {
        deep: true,
        immediate: true,
    });

    return {
        matchId,
        companyRef,
        order,
        isAnonymous,
        isLocal,
        isInterstate,
        isOrganic,
        isCustom,
        isTender,
        isLane,
        isTrip,
        isFixed,
        isVariable,
        isVisible,
        isPremium,
        moveType,
        moveId,
        matchType,
        companyName,
        companyId,
        companyCount,
        logoUrl,
        pricePerUnit,
        pricePerHour,
        quotedSpace,
        inputPrice,
        rating,
        reviewCount,
        estimatedTotal,
        minPrice,
        callOutFee,
        matchableType,
        matchableId,
        matchableFormatted,
        matchableIdFormatted,
        routeDeliveryTimeframe,
        bundles,
        minHours,
        pickupDateFormatted,
        deliveryDateFormatted,
        spaceTotal,
        companyTotal,
        surcharge,
        extraSurcharge,
        bundlesCost,
        muvalFee,
        bookingFee,
        reviewPricing,
        companyNotes,
        createdAt,
        expiresAt,
        minTime,
    };

    // helper functions
    function handleMatchableType(matchable_type, matchable) {
        switch (matchable_type) {
            case 'trip':
                return 'Trip';
            case 'lane':
                return 'Lane';
            case 'move_custom_quote':
                return 'Custom';
            case 'move_tender':
                return 'Tender';
            case 'depot':
                return matchable?.localPrices?.id ? 'Local' : 'Depot';
        }
    }

    function handleMatchableId(matchable_type, matchable) {
        return matchable_type === 'depot' && matchable?.localPrices?.id ? matchable.localPrices.id : matchable?.id;
    }
}
